/**
 * JournalPreviewPanel — separate tab when JournalPreview is not inline (accented card).
 */
import React from 'react'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import type { FormNode } from '../designer/types/form.types'
import { FormRenderer } from '../shared/FormRenderer'
import { TableElement } from '../designer/components/editors/FormEditor/elements/TableElement'
import { useJournalPreviewRows } from './useJournalPreviewRows'

interface JournalPreviewPanelProps {
  connectionId: string
  objectName: string
  recordId: string
  journalGroup: FormNode
  documentData: Record<string, unknown>
  linesPartName?: string
  reloadKey?: number
  chartOfAccountsTarget?: string
}

export function JournalPreviewPanel({
  connectionId,
  objectName,
  recordId,
  journalGroup,
  documentData,
  linesPartName = 'Lines',
  reloadKey = 0,
  chartOfAccountsTarget,
}: JournalPreviewPanelProps) {
  const { loading, rowsByTable, hasPostedMovements } = useJournalPreviewRows({
    connectionId,
    objectName,
    recordId,
    journalGroup,
    documentData,
    linesPartName,
    reloadKey,
    chartOfAccountsTarget,
  })

  const renderTable = (name: string, node: FormNode) => {
    const rows = rowsByTable[name] ?? []
    return <TableElement node={{ ...node, staticPreviewRows: rows }} preview />
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  const hasAnyRows = Object.values(rowsByTable).some((r) => r.length > 0)

  return (
    <Box>
      {!hasPostedMovements && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Enregistrez le document pour afficher les écritures issues des mouvements de registre.
          L&apos;aperçu ci-dessous reflète les lignes calculées ou enregistrées.
        </Alert>
      )}
      {!hasAnyRows && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Aucune écriture à afficher. Vérifiez le montant, les comptes et enregistrez le document.
        </Typography>
      )}
      <FormRenderer
        root={[journalGroup]}
        mode="app"
        callbacks={{
          renderTable: (tableName, node, opts) =>
            opts.mode === 'app' ? renderTable(tableName, node) : null,
        }}
      />
    </Box>
  )
}
